/**
 * useUserLessons — the signed-in member's lesson packs at the current club.
 *
 * Plain `useQuery` over the envelope: `success: false` is data, read from
 * `failure`, not `isError` (docs/conventions/internalapi-envelope.md). Club
 * and user resolve here; any status counts as holding a pack.
 */

import { useQuery, replaceEqualDeep, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import type { ApiErrorResult, ApiResult } from '~/lib/apiClient/types';
import { useClubId } from '~/services/appRuntime';
import { useAuth } from '~/services/auth';
import { safeToNumericUserId } from '~/lib/utils/userIdUtils';
import { getUserLessons } from '../api/getUserLessons';
import type { GetUserLessonsPayload, UserLesson } from '../types';

type UserLessonsResult = ApiResult<GetUserLessonsPayload>;

export interface UseUserLessonsOptions {
  enabled?: boolean;
}

export type UseUserLessonsResult = UseQueryResult<UserLessonsResult, Error> & {
  /** The member's packs at this club, newest first. Never undefined. */
  userLessons: UserLesson[];
  /** An answer arrived at least once. False on first load, disabled, thrown, or paused. */
  hasAnswer: boolean;
  /** The refusal envelope when the latest answer is `success: false`. */
  failure: ApiErrorResult | null;
};

// Module scope — referential stability (docs/conventions/react-state.md).
const EMPTY_USER_LESSONS: UserLesson[] = [];

/**
 * A failed refetch keeps the last good packs instead of blanking them — a
 * stale answer is still an answer (react-state.md). Only a first-load
 * failure reaches `failure`.
 */
function keepLastSuccess(previous: unknown, next: unknown): unknown {
  const prev = previous as UserLessonsResult | undefined;
  const incoming = next as UserLessonsResult;
  if (prev?.success && !incoming.success) return prev;
  return replaceEqualDeep(previous, next);
}

export function useUserLessons(options: UseUserLessonsOptions = {}): UseUserLessonsResult {
  const { enabled = true } = options;
  const { user, isAuthenticated } = useAuth();
  const userId = isAuthenticated ? safeToNumericUserId(user?.id) : null;
  const [clubIdParam] = useClubId();
  const clubId = clubIdParam ? Number(clubIdParam) : null;

  const query = useQuery({
    queryKey:
      clubId && userId ? queryKeys.lessons.user(clubId, userId) : queryKeys.disabled.lessons(),
    queryFn: () => {
      if (!clubId || !userId) throw new Error('clubId and userId are required');
      return getUserLessons(userId, { clubId });
    },
    enabled: enabled && !!clubId && !!userId,
    // Balances are drawn down by bookings made elsewhere.
    ...cachePresets.frequent,
    structuralSharing: keepLastSuccess,
  });

  const result = query.data;

  return {
    ...query,
    userLessons: result?.success
      ? (result.payload.userLessons ?? EMPTY_USER_LESSONS)
      : EMPTY_USER_LESSONS,
    hasAnswer: result !== undefined,
    failure: result && !result.success ? result : null,
  };
}
